import { useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { DollarSign, Plus } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { PageShell } from '@/components/common/PageShell'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { EmptyState } from '@/components/common/EmptyState'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { PermissionGate } from '@/components/common/PermissionGate'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { Badge } from '@/components/ui/Badge'
import { usePermissions } from '@/hooks/usePermissions'
import { useCompany } from '@/features/companies/hooks/useCompanies'
import { usePayrollRuns, useCreateRun } from '@/features/payroll/hooks/usePayrollRuns'
import { StatusBadge } from './StatusBadge'
import type { PayrollRun, PayrollRunStatus, PayrollRunType } from '@/features/payroll/types/payroll.types'

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'calculated', label: 'Calculated' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
  { value: 'rejected', label: 'Rejected' },
]

const RUN_TYPE_OPTIONS = [
  { value: 'batch', label: 'Batch' },
  { value: 'off_cycle', label: 'Off-cycle' },
  { value: 'correction', label: 'Correction' },
  { value: 'final_settlement', label: 'Final Settlement' },
]

const RUN_TYPE_VARIANT: Record<string, 'neutral' | 'info' | 'warning' | 'danger'> = {
  batch: 'neutral',
  off_cycle: 'info',
  correction: 'warning',
  final_settlement: 'danger',
}

const currentYear = new Date().getFullYear()
const YEAR_OPTIONS = [
  { value: '', label: 'All years' },
  ...Array.from({ length: 5 }, (_, i) => {
    const y = String(currentYear - 2 + i)
    return { value: y, label: y }
  }),
]
const MONTH_OPTIONS = [
  { value: '', label: 'All months' },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: new Date(2000, i).toLocaleString(undefined, { month: 'long' }),
  })),
]

interface CreateFormValues {
  year: number
  month: number
  runType: PayrollRunType
  reason: string
  baseCurrency: string
  notes: string
}

export function RunListPage(): React.ReactElement {
  const { companyId } = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [createOpen, setCreateOpen] = useState(false)
  usePermissions()
  const { data: company } = useCompany(companyId ?? null)
  const companyName = company?.name ?? 'Company'

  const page = Number(searchParams.get('page')) || 1
  const filterYear = searchParams.get('year') ? Number(searchParams.get('year')) : undefined
  const filterMonth = searchParams.get('month') ? Number(searchParams.get('month')) : undefined
  const filterStatus = (searchParams.get('status') || undefined) as PayrollRunStatus | undefined

  const { data, isLoading } = usePayrollRuns(companyId ?? null, {
    page,
    limit: 20,
    year: filterYear,
    month: filterMonth,
    status: filterStatus,
  })

  const createMut = useCreateRun()

  const form = useForm<CreateFormValues>({
    defaultValues: {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      runType: 'batch',
      reason: '',
      baseCurrency: '',
      notes: '',
    },
  })
  const watchedRunType = form.watch('runType')

  if (!companyId) {
    navigate('/companies', { replace: true })
    return <></>
  }

  const runs = data?.data ?? []
  const total = data?.total ?? 0
  const limit = data?.limit ?? 20
  const hasFilters = !!filterYear || !!filterMonth || !!filterStatus

  const updateParam = (key: string, val: string): void => {
    const next = new URLSearchParams(searchParams)
    if (val) next.set(key, val)
    else next.delete(key)
    next.delete('page')
    setSearchParams(next)
  }

  const clearFilters = (): void => setSearchParams({})

  const handleCreate = (values: CreateFormValues): void => {
    createMut.mutate(
      {
        companyId,
        body: {
          year: values.year,
          month: values.month,
          runType: values.runType,
          reason: values.runType !== 'batch' ? values.reason : undefined,
          baseCurrency: values.baseCurrency || undefined,
          notes: values.notes || undefined,
        },
      },
      {
        onSuccess: (run) => {
          setCreateOpen(false)
          form.reset()
          navigate(`/companies/${companyId}/payroll/runs/${run._id}`)
        },
      },
    )
  }

  const period = (run: PayrollRun): string =>
    `${run.payrollMonth.year}-${String(run.payrollMonth.month).padStart(2, '0')}`

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Organization', to: '/dashboard' },
        { label: 'Companies', to: '/companies' },
        { label: companyName, to: `/companies/${companyId}` },
        { label: 'Payroll Runs' },
      ]}
      icon={DollarSign}
      title="Payroll Runs"
      description={`${companyName} payroll runs`}
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          actions={
            <PermissionGate module="payroll" action="run">
              <Button size="sm" className="h-11 rounded-xl" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" />
                <span className="ml-1.5">New Payroll Run</span>
              </Button>
            </PermissionGate>
          }
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <Select
              placeholder="Year"
              options={YEAR_OPTIONS}
              value={searchParams.get('year') ?? ''}
              onChange={(v) => updateParam('year', v)}
              className="w-28"
            />
            <Select
              placeholder="Month"
              options={MONTH_OPTIONS}
              value={searchParams.get('month') ?? ''}
              onChange={(v) => updateParam('month', v)}
              className="w-40"
            />
            <Select
              placeholder="Status"
              options={STATUS_OPTIONS}
              value={searchParams.get('status') ?? ''}
              onChange={(v) => updateParam('status', v)}
              className="w-36"
            />
            {hasFilters && (
              <Button variant="ghost" size="sm" className="h-11" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        </WorkspaceToolbar>

        {/* Table */}
        {isLoading ? (
          <TableSkeleton rows={5} cols={6} />
        ) : runs.length === 0 ? (
          <EmptyState
            icon={DollarSign}
            title={hasFilters ? 'No runs match your filters' : 'No payroll runs yet'}
            description={
              hasFilters ? undefined : 'Create your first payroll run to get started.'
            }
            action={
              hasFilters ? (
                <Button variant="secondary" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-4 py-3">Period</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3 text-right">Employees</th>
                    <th className="px-4 py-3 text-right">Gross</th>
                    <th className="px-4 py-3 text-right">Net</th>
                    <th className="px-4 py-3">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run: PayrollRun) => (
                    <tr
                      key={run._id}
                      className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/30"
                      onClick={() =>
                        navigate(`/companies/${companyId}/payroll/runs/${run._id}`)
                      }
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                        {period(run)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={run.status} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={RUN_TYPE_VARIANT[run.runType] ?? 'neutral'}>
                          {run.runType.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{run.employeeCount}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {(run.totals?.grossPay ?? 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium">
                        {(run.totals?.netPay ?? 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                        {new Date(run.createdAt).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > limit && (
              <TablePaginationBar
                page={page}
                limit={limit}
                total={total}
                totalPages={Math.ceil(total / limit)}
                onPageChange={(p) => {
                  const next = new URLSearchParams(searchParams)
                  next.set('page', String(p))
                  setSearchParams(next)
                }}
              />
            )}
          </>
        )}
      </div>

      {/* Create modal */}
      <Modal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false)
          form.reset()
        }}
        title="New Payroll Run"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setCreateOpen(false)
                form.reset()
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={createMut.isPending}
              onClick={form.handleSubmit(handleCreate)}
            >
              Create Run
            </Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={form.handleSubmit(handleCreate)}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Year
              </label>
              <input
                type="number"
                min={2000}
                max={2100}
                className="h-8 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                {...form.register('year', { valueAsNumber: true })}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Month
              </label>
              <input
                type="number"
                min={1}
                max={12}
                className="h-8 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                {...form.register('month', { valueAsNumber: true })}
              />
            </div>
          </div>
          <Select
            label="Run Type"
            options={RUN_TYPE_OPTIONS}
            value={watchedRunType}
            onChange={(v) => form.setValue('runType', v as PayrollRunType)}
          />
          {watchedRunType !== 'batch' && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                rows={2}
                placeholder="Required for non-batch runs"
                {...form.register('reason', {
                  required: true,
                })}
              />
              {form.formState.errors.reason && (
                <p className="mt-1 text-xs text-red-600">Reason is required</p>
              )}
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Notes
            </label>
            <textarea
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              rows={2}
              placeholder="Optional"
              {...form.register('notes')}
            />
          </div>
        </form>
      </Modal>
    </PageShell>
  )
}

RunListPage.displayName = 'RunListPage'
